import { useGetCenterApplicationsQuery } from '../../redux/services/centerApplicationService'
import { Link } from 'react-router-dom'
import { CenterApplicationInfoOut } from '../../types'

function ApplicationCard({
  application
}: {
  application: CenterApplicationInfoOut
}) {
  const centerTypeColors = {
    pediatric: 'bg-[#A0B2B4] text-white',
    adult: 'bg-[#AF8A48] text-white',
    whole_life: 'bg-[#713d43] text-white'
  }

  const centerTypeLabels = {
    pediatric: 'PEDIATRIC',
    adult: 'ADULT',
    whole_life: 'WHOLE LIFE'
  }

  const totalSCPatients =
    application.number_of_adult_sc + application.number_of_pediatric_sc
  const totalSCAPatients =
    application.number_of_adult_sca + application.number_of_pediatric_sca

  return (
    <Link
      to={`/admin/applications/${application.id}`}
      className="block bg-[#EDE6DE] rounded-lg hover:shadow-lg transition-all p-6 relative group"
    >
      <div className="flex justify-between items-start">
        <div className="flex-1 pr-4">
          <h3 className="text-2xl font-normal text-[#3C2416] mb-3">
            {application.center_name}
          </h3>

          <span
            className={`inline-block px-4 py-1.5 rounded text-xs font-semibold tracking-wider ${
              centerTypeColors[application.center_type]
            } mb-4`}
          >
            {centerTypeLabels[application.center_type]}
          </span>

          <div className="text-sm text-[#3C2416] space-y-1">
            <p className="opacity-80">
              {application.city}, {application.state}
            </p>
            <div className="flex gap-6 mt-2">
              <div>
                <span className="opacity-60">SC Patients:</span>{' '}
                <span className="font-medium">{totalSCPatients}</span>
              </div>
              <div>
                <span className="opacity-60">SCA Patients:</span>{' '}
                <span className="font-medium">{totalSCAPatients}</span>
              </div>
            </div>
          </div>

          {application.first_approval_by && !application.second_approval_by && (
            <div className="mt-3 text-xs text-[#A6410A] font-medium">
              Awaiting second approval
            </div>
          )}
        </div>

        <div className="flex items-center text-[#A6410A] group-hover:translate-x-1 transition-transform">
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </div>
      </div>
    </Link>
  )
}

export default function AdminDashboard() {
  const {
    data: applications = [],
    isLoading,
    error
  } = useGetCenterApplicationsQuery({ status: 'pending' })

  if (isLoading) {
    return (
      <div className="bg-[#F5F5F5] min-h-screen p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-300 rounded w-1/4 mb-8"></div>
          <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-[#EDE6DE] rounded-lg p-6">
                <div className="h-6 bg-gray-300 rounded mb-4 w-3/4"></div>
                <div className="h-4 bg-gray-300 rounded w-1/4 mb-3"></div>
                <div className="h-4 bg-gray-300 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-[#F5F5F5] min-h-screen p-8">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-2xl font-medium text-[#A6410A] mb-8">
            Top Pending Applications
          </h1>
          <div className="bg-white rounded-lg p-8">
            <p className="text-red-600 text-lg mb-4">
              Error loading applications
            </p>
            <p className="text-gray-600">
              There was an issue retrieving the application data. This may be
              due to a data format mismatch in the backend API.
            </p>
            <details className="mt-4">
              <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                Technical details
              </summary>
              <pre className="mt-2 text-xs bg-gray-100 p-4 rounded overflow-auto">
                {JSON.stringify(error, null, 2)}
              </pre>
            </details>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-[#F5F5F5] min-h-screen p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-2xl font-medium text-[#A6410A] mb-8">
          Top Pending Applications
        </h1>

        {applications.length === 0 ? (
          <div className="bg-white rounded-lg p-12 text-center">
            <p className="text-gray-500 text-lg">
              No pending applications at this time.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {applications.map((application) => (
              <ApplicationCard key={application.id} application={application} />
            ))}
          </div>
        )}

        {applications.length > 0 && (
          <div className="mt-8 text-center">
            <Link
              to="/admin/pending-applications"
              className="inline-flex items-center text-[#A6410A] hover:text-[#894d35] font-medium"
            >
              View All Applications
              <svg
                className="w-4 h-4 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}
